import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {getAll} from '../../../../app/services/common_requests'
import {ID} from '../../../helpers'
import {useIntl} from 'react-intl'
import {parseTagInput, mergeUniqueTags} from '../../../../app/pages/tours-management/tour-details/core/tagInputHelpers'
import {pickTranslatedLabel} from '../../../../app/pages/pages-managment/pages-details/core/contentLanguageUtils'

interface TagsComponentProps {
  initialValues: string[]
  onValuesChange: (values: string[]) => void
  type: 'themes' | 'options' | 'metaKeywords' | 'facilities'
  selectedLanguage: string
}

/** Extrait une liste depuis les réponses API (liste plate ou StandardListRes). */
function extractListRows(body: unknown): unknown[] {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (typeof body === 'object' && body !== null) {
    const o = body as { data?: unknown }
    if (Array.isArray(o.data)) return o.data
    const nested = (o.data as { data?: unknown[] } | undefined)?.data
    if (Array.isArray(nested)) return nested
  }
  return []
}

const TagsComponent: React.FC<TagsComponentProps> = ({
  initialValues,
  onValuesChange,
  type,
  selectedLanguage,
}) => {
  const [values, setValues] = useState<string[]>([])
  const [availableValues, setAvailableValues] = useState<Array<{id: string; name: string}>>([])
  const [selectedValue, setSelectedValue] = useState<string>('')
  const [newKeyword, setNewKeyword] = useState<string>('')
  const intl = useIntl()

  const incomingIdsKey = useMemo(() => {
    try {
      return JSON.stringify([...(initialValues || [])].sort())
    } catch {
      return String(initialValues)
    }
  }, [initialValues])

  useEffect(() => {
    setValues([...(initialValues || [])].filter((val): val is string => typeof val === 'string'))
  }, [incomingIdsKey])

  const reloadSourceLists = useCallback(() => {
    if (type === 'metaKeywords') return

    const listParams = {
      /** 'all' : le backend peuplera toutes les traductions ; le middleware évite une fusion destructive. */
      language: 'all' as const,
      page: 0,
      size: 500,
    }

    const langPick = selectedLanguage?.trim()?.toLowerCase() || 'fr'

    const runTheme = async () => {
      const payload = await getAll(listParams, '/themes').catch(() => null)
      const rows = extractListRows(payload) as {_id: ID; translations?: { name: string; language?: string }[]}[]
      setAvailableValues(
        rows.map((theme) => ({
          id: String(theme._id),
          name: pickTranslatedLabel(
            theme.translations?.map((t) => ({ language: t.language, name: t.name })),
            langPick,
          ),
        })),
      )
    }

    const runOption = async () => {
      const payload = await getAll(listParams, '/options').catch(() => null)
      const rows = extractListRows(payload) as {
        _id: ID
        translations?: { title: string; language?: string }[]
      }[]
      setAvailableValues(
        rows.map((option) => ({
          id: String(option._id),
          name: pickTranslatedLabel(
            option.translations?.map((t) => ({
              language: t.language,
              title: t.title,
              name: t.title,
            })),
            langPick,
          ),
        })),
      )
    }

    const runFacility = async () => {
      const payload = await getAll(listParams, '/facilities').catch(() => null)
      const rows = extractListRows(payload) as {
        _id: ID
        title?: string
        translations?: { title: string; language?: string }[]
      }[]
      setAvailableValues(
        rows.map((facility) => ({
          id: String(facility._id),
          name: pickTranslatedLabel(
            facility.translations?.map((t) => ({
              language: t.language,
              title: t.title,
              name: t.title,
            })),
            langPick,
            facility.title,
          ),
        })),
      )
    }

    if (type === 'themes') void runTheme()
    else if (type === 'options') void runOption()
    else if (type === 'facilities') void runFacility()
  }, [selectedLanguage, type])

  useEffect(() => {
    void reloadSourceLists()
  }, [reloadSourceLists])

  const handleAddValue = () => {
    if (type === 'metaKeywords') {
      const parsed = parseTagInput(newKeyword)
      if (parsed.length) {
        const updatedValues = mergeUniqueTags(values, parsed)
        setValues(updatedValues)
        onValuesChange(updatedValues)
        setNewKeyword('')
      }
    } else {
      if (selectedValue.trim() && !values.includes(selectedValue.trim())) {
        const updatedValues = [...values, selectedValue.trim()]
        setValues(updatedValues)
        onValuesChange(updatedValues)
        setSelectedValue('')
      }
    }
  }

  const handleDeleteValue = (index: number) => {
    const updatedValues = values.filter((_, i) => i !== index)
    setValues(updatedValues)
    onValuesChange(updatedValues)
  }

  return (
    <div data-tags-type={type} className='tags-component position-relative' style={{zIndex: 1}}>
      {type === 'metaKeywords' ? (
        <>
          <div className='mb-3'>
            {values.map((id, index) => {
              const sid = String(id)
              const item = availableValues.find((v) => String(v.id) === sid)
              return (
                <span key={`${sid}-${index}`} className='badge badge-light me-2'>
                  {item?.name || sid}
                  <button
                    type='button'
                    className='btn-close ms-2'
                    aria-label='Delete'
                    onClick={() => handleDeleteValue(index)}
                  />
                </span>
              )
            })}
          </div>

          <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
            <input
              type='text'
              className='form-control flex-grow-1 min-w-200px'
              style={{maxWidth: '100%'}}
              placeholder={intl.formatMessage({
                id: 'COLUMNS.SELECT_SEO_KEYWORDS',
              })}
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newKeyword.trim()) {
                  e.preventDefault()
                  handleAddValue()
                }
              }}
            />
            <button type='button' className='btn btn-primary' onClick={handleAddValue}>
              {intl.formatMessage({
                id: 'COLUMNS.SELECT_SEO_KEYWORDS',
              })}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Liste déroulante + action en premier : évite que beaucoup de badges masquent les contrôles (équipements). */}
          <div className='bo-select-row d-flex align-items-center flex-wrap gap-2 mb-4'>
            <select
              className='form-select flex-grow-1 min-w-200px'
              style={{maxWidth: '100%', width: '100%'}}
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
              aria-label={
                type === 'themes'
                  ? intl.formatMessage({id: 'COLUMNS.SELECT_THEME'})
                  : type === 'facilities'
                    ? intl.formatMessage({
                        id: 'COLUMNS.SELECT_FACILITY',
                        defaultMessage: 'Select facility',
                      })
                  : intl.formatMessage({id: 'COLUMNS.SELECT_OPTION'})
              }
            >
              <option value=''>
                {type === 'themes'
                  ? intl.formatMessage({id: 'COLUMNS.SELECT_THEME'})
                  : type === 'facilities'
                    ? intl.formatMessage({
                        id: 'COLUMNS.SELECT_FACILITY',
                        defaultMessage: 'Select facility',
                      })
                  : intl.formatMessage({id: 'COLUMNS.SELECT_OPTION'})}
              </option>
              {availableValues
                .filter((item) =>
                  !values.some((v) => String(v) === String(item.id) || String(v) === String(item.name)),
                )
                .map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.name || item.id}
                  </option>
                ))}
            </select>
            <button type='button' className='btn btn-primary' onClick={handleAddValue}>
              {type === 'themes'
                ? intl.formatMessage({id: 'COLUMNS.ADD_THEME'})
                : type === 'facilities'
                  ? intl.formatMessage({
                      id: 'COLUMNS.ADD_FACILITY',
                      defaultMessage: 'Add facility',
                    })
                : intl.formatMessage({id: 'COLUMNS.ADD_OPTION'})}
            </button>
          </div>

          <div className='mb-2'>
            {values.map((id, index) => {
              const sid = String(id)
              const item = availableValues.find((v) => String(v.id) === sid)
              return (
                <span key={`${sid}-${index}`} className='badge badge-light me-2 mb-2'>
                  {item?.name || sid}
                  <button
                    type='button'
                    className='btn-close ms-2'
                    aria-label='Delete'
                    onClick={() => handleDeleteValue(index)}
                  />
                </span>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default TagsComponent
